import {
  createTaskList,
  CreateTaskListMutation,
  CreateTaskListMutationVariables,
  deleteTaskList,
  DeleteTaskListMutation,
  DeleteTaskListMutationVariables,
  graphqlCall,
  IngredientItem,
  TaskList,
  updateTaskList,
  UpdateTaskListMutation,
  UpdateTaskListMutationVariables,
} from '@akin-home/amplify';

import {
  CheckListItem,
  ChecklistModel,
  checklistModelToCreateGraphql,
  checklistModelToGraphql,
} from '@akin-home/checklists';

import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { checklistKeys } from './queries';

export const updateTaskListMutation = (
  params: UpdateTaskListMutationVariables
) => graphqlCall<UpdateTaskListMutation>(updateTaskList, params);

export const createTaskListMutation = (
  params: CreateTaskListMutationVariables
) => graphqlCall<CreateTaskListMutation>(createTaskList, params);

export const deleteTaskListMutation = (
  params: DeleteTaskListMutationVariables
) => graphqlCall<DeleteTaskListMutation>(deleteTaskList, params);

export const useCreateTaskListMutation = (options?: any) =>
  useMutation(createTaskListMutation, options);

export const useUpdateTaskListMutation = (options?: any) =>
  useMutation(updateTaskListMutation, options);

export const useDeleteTaskListMutation = (options?: any) =>
  useMutation(deleteTaskListMutation, options);

export const useCreateTaskList = (options?: any) => {
  const queryClient = useQueryClient();

  const create = useCreateTaskListMutation({
    onSuccess: () => queryClient.invalidateQueries(checklistKeys.lists()),
  });

  return useCallback(
    (list: ChecklistModel) => {
      const input = checklistModelToCreateGraphql(list);

      return create.mutateAsync({ input });
    },
    [create]
  );
};

export const useUpdateTaskListFromModel = (options?: any) => {
  const queryClient = useQueryClient();

  const update = useUpdateTaskListMutation({
    onMutate: (vars: UpdateTaskListMutationVariables) => {
      const update_data = {
        ...vars.input,
        updatedAt: new Date().toISOString(),
      };

      queryClient.setQueriesData<TaskList[]>(
        checklistKeys.lists(),
        (data = []) => {
          return data.map((item) =>
            item.id === vars.input?.id
              ? ({ ...item, ...update_data } as TaskList)
              : item
          );
        }
      );

      queryClient.setQueryData<TaskList>(
        checklistKeys.detail(vars.input?.id as string),
        (data) => {
          return { ...data, ...update_data } as TaskList;
        }
      );
    },
  });

  return useCallback(
    (list: ChecklistModel) =>
      update.mutateAsync({ input: checklistModelToGraphql(list) }),
    [update]
  );
};

export const useUpdateTaskListItem = (options?: any) => {
  const queryClient = useQueryClient();

  const update = useUpdateTaskListMutation({
    onMutate: (vars: UpdateTaskListMutationVariables) => {
      const input_data = {
        ...vars.input,
        updatedAt: new Date().toISOString(),
      };

      queryClient.setQueriesData<TaskList[]>(
        checklistKeys.lists(),
        (data = []) => {
          return data.map((item) =>
            item.id === vars.input?.id
              ? ({ ...item, ...input_data } as TaskList)
              : item
          );
        }
      );

      const data = queryClient.getQueryData<TaskList>(
        checklistKeys.detail(vars.input?.id as string)
      );

      /** Only update if an existing item was found. Otherwise functionality becomes weird */
      if (data) {
        queryClient.setQueryData<TaskList | undefined>(
          checklistKeys.detail(vars.input?.id as string),
          (data) => {
            return { ...data, ...input_data } as TaskList;
          }
        );
      }
    },
  });

  return useCallback(
    ({
      id,
      checklist,
    }: {
      id: string;
      checklist: CheckListItem[] | IngredientItem[];
    }) =>
      update.mutate({ input: { id, checklist: JSON.stringify(checklist) } }),
    [update]
  );
};

export const useDeleteTaskList = () => {
  const queryClient = useQueryClient();

  const remove = useDeleteTaskListMutation({
    onMutate: (vars: { input: { id: string } }) => {
      queryClient.setQueriesData<TaskList[]>(
        checklistKeys.lists(),
        (data = []) => {
          return data.filter((item) => item.id !== vars.input.id);
        }
      );

      queryClient.removeQueries(checklistKeys.detail(vars.input.id));
    },
  });

  return useCallback(
    (id: string) => remove.mutateAsync({ input: { id } }),
    [remove]
  );
};
